/**
 * @file usePagination.ts
 * @description
 * -----------------------------------------------------
 * Hook personalizado para crear la función de cambio de paginación en las tablas
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-05-21
 * @author DLTCode
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { Dispatch, useCallback, useMemo } from "react";
import { OnChangeFn, PaginationState } from "@tanstack/react-table";
import { DEFAULT_PAGE, DEFAULT_SIZE } from "@/constants/filters";
import { WithPagination } from "@/types/tableFiltersPagination/filters";
import { uncapitalize } from "@/lib/text";
import { BaseFilters } from "@/enums/baseFilters.enum";
import useFilters from "@/hooks/useFilters";

/**
 * Hook genérico de paginación.
 * Recibe la sección para llamar a useFilters y obtener los filtros actuales y la función para actualizarlos y actualizar la URL con los search params de paginación.
 * Recibe un prefijo opcional para los nombres de page / pageSize. Útil para cuando hay varias tablas en la misma página.
 * Despues llama a useBasePagination con los filtros, la función y el prefijo.
 * @param section  Clave (enum) que indica qué conjunto de filtros usar
 * @param prefix   (opcional) prefijo para los nombres de page / pageSize
 * @returns        `[pagination, onPaginationChange]`
 */
export default function usePagination(section, t0) {
  const prefix = t0 === undefined ? "" : t0;
  const [filters, setFilters] = useFilters(section);
  return useBasePagination(filters, setFilters, prefix);
}

/**
 * Hook para manejar cambios de paginación en tablas
 * @param filters Objeto con filtros actuales de paginación
 * @param setFilters Función para actualizar los filtros de paginación
 * @param prefix
 * @returns Estado de paginación y función para actualizarlo
 */
function useBasePagination<P extends string | undefined = undefined, TFilters extends WithPagination<P> = WithPagination<P>>(filters: TFilters, setFilters: Dispatch<Partial<TFilters>>, prefix: P) {
  prefix = (prefix || "") as P;

  // Claves para page y pageSize, con el prefijo si lo hay para que no haya conflicto entre varias tablas
  const pageKey = useMemo(() => uncapitalize(`${prefix}Page`) as keyof TFilters, [prefix]);
  const sizeKey = useMemo(() => uncapitalize(`${prefix}PageSize`) as keyof TFilters, [prefix]);

  /**
   * Estado de paginación actual. Si no hay nada en los filtros, usa los valores por defecto.
   * Si hay, los convierte a número (ya que vienen como string en los search params)
   * @returns {PaginationState} El estado de paginación actual (pageIndex, pageSize)
   * Resta 1 a pageIndex porque la tabla usa base 0 y la API/base de datos base 1
   */
  const pagination = useMemo(() => {
    const pageValue = filters[pageKey];
    const sizeValue = filters[sizeKey];
    return {
      pageIndex: pageValue ? Number(pageValue) - 1 : DEFAULT_PAGE - 1,
      pageSize: sizeValue ? Number(sizeValue) : DEFAULT_SIZE
    } as PaginationState;
  }, [filters, pageKey, sizeKey]);

  /**
   * Función para manejar cambios de paginación. (Página o número de elementos por página)
   * Obtiene los valores actuales en base 0, los convierte a base 1 y llama a setFilters para actualizar los filtros y la URL
   */
  const onPaginationChange = useCallback<OnChangeFn<Partial<PaginationState>>>(updaterOrValue => {
    const currentPageIndex = filters[pageKey] ? Number(filters[pageKey]) - 1 // De base 1 a base 0
    : DEFAULT_PAGE - 1;
    const currentPageSize = filters[sizeKey] ? Number(filters[sizeKey]) : DEFAULT_SIZE;
    const paginationValue = typeof updaterOrValue === "function" ? updaterOrValue({
      pageIndex: currentPageIndex,
      pageSize: currentPageSize
    }) : updaterOrValue;

    // Convertir pageIndex base 0 de vuelta a base 1 para la API/filtros
    const updatedValues = {
      [pageKey]: paginationValue?.pageIndex != null ? paginationValue?.pageIndex + 1 // Sumar 1 para convertir de base 0 a base 1
      : filters[pageKey],
      [sizeKey]: paginationValue.pageSize || filters[sizeKey]
    } as Partial<TFilters>;
    setFilters(updatedValues);
  }, [filters, setFilters, pageKey, sizeKey]);

  // Devuelve la paginación actual y la función para cambiarla
  return [pagination, onPaginationChange] as const;
}